import { createAsyncThunk } from '@reduxjs/toolkit';

import { maskCnpjParaLog } from '@/shared/utils/cnpj';
import { reportError } from '@/shared/utils/reportError';
import type { RootState } from '@/store';

import { MENSAGEM_ACEITE_OBRIGATORIO, SELF_TERMOS_VERSAO } from '../constants';
import type {
  BuscaContextoResultado,
  CampoParametroSelf,
  ConsultaCnpjNegociacaoSelfResultado,
  ConsultaPagamentoResultado,
  CriarAgenteResultado,
  IniciarPagamentoResultado,
  ObjetivoOpcao,
  ObjetivoSelf,
  PlanoNegociacaoSelf,
} from '../types';
import { limparSessaoSelf, salvarSessaoSelf } from '../utils/sessaoSelf';
import { negociacaoSelfService } from './services';

const mensagemDeErro = (error: unknown, fallback: string): string =>
  error instanceof Error ? error.message : fallback;

export const carregarObjetivosSelf = createAsyncThunk<
  ObjetivoOpcao[],
  void,
  { rejectValue: string }
>('negociacaoSelf/carregarObjetivos', async (_, { rejectWithValue }) => {
  try {
    return await negociacaoSelfService.carregarObjetivos();
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao carregar os objetivos'));
  }
});

export const carregarPlanosSelf = createAsyncThunk<
  PlanoNegociacaoSelf[],
  void,
  { rejectValue: string }
>('negociacaoSelf/carregarPlanos', async (_, { rejectWithValue }) => {
  try {
    return await negociacaoSelfService.carregarPlanos();
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao carregar os planos'));
  }
});

export const consultarCnpjNegociacaoSelf = createAsyncThunk<
  ConsultaCnpjNegociacaoSelfResultado,
  string,
  { rejectValue: string }
>('negociacaoSelf/consultarCnpj', async (cnpj, { signal, rejectWithValue }) => {
  try {
    return await negociacaoSelfService.consultarCnpj(cnpj, signal);
  } catch (error) {
    const mensagem = mensagemDeErro(error, 'Erro ao consultar o CNPJ');
    if (!signal.aborted) {
      reportError(error instanceof Error ? error : new Error(mensagem), {
        source: 'negociacaoSelf.consultarCnpj',
        cnpj: maskCnpjParaLog(cnpj),
      });
    }
    return rejectWithValue(mensagem);
  }
});

export const buscarContextoEmpresaSelf = createAsyncThunk<
  BuscaContextoResultado,
  void,
  { rejectValue: string; state: RootState }
>('negociacaoSelf/buscarContexto', async (_, { getState, rejectWithValue }) => {
  try {
    const { perfil, objetivo } = getState().negociacaoSelf;
    const resultado = await negociacaoSelfService.buscarContextoEmpresa(perfil);
    salvarSessaoSelf({
      leadId: resultado.leadId,
      accessToken: resultado.accessToken,
      objetivo: objetivo ?? undefined,
    });
    return resultado;
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao buscar contexto da empresa'));
  }
});

export const carregarCamposObjetivoSelf = createAsyncThunk<
  { objetivo: ObjetivoSelf; campos: CampoParametroSelf[] },
  ObjetivoSelf,
  { rejectValue: string }
>('negociacaoSelf/carregarCampos', async (objetivo, { rejectWithValue }) => {
  try {
    const campos = await negociacaoSelfService.carregarCamposObjetivo(objetivo);
    return { objetivo, campos };
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao carregar os parâmetros do objetivo'));
  }
});

export const criarAgenteSelf = createAsyncThunk<
  CriarAgenteResultado,
  void,
  { rejectValue: string; state: RootState }
>('negociacaoSelf/criarAgente', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { leadId, accessToken, objetivo, perfil, valoresParametros } = getState().negociacaoSelf;
    if (!leadId || !accessToken || !objetivo) {
      return rejectWithValue('Configuração incompleta. Volte ao início do fluxo.');
    }
    return await negociacaoSelfService.criarAgente(
      { leadId, accessToken, objetivo, sobre: perfil.sobre, valores: valoresParametros },
      signal,
    );
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao criar o agente'));
  }
});

export const enviarMensagemSimulacaoSelf = createAsyncThunk<
  string,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoSelf/enviarMensagem', async (mensagem, { getState, rejectWithValue }) => {
  try {
    const { leadId, accessToken, chatMensagens } = getState().negociacaoSelf;
    if (!leadId || !accessToken) {
      return rejectWithValue('Configuração incompleta. Volte ao início do fluxo.');
    }
    const ultimaEhMensagemAtual =
      chatMensagens.at(-1)?.autor === 'user' && chatMensagens.at(-1)?.texto === mensagem;
    const historico = (ultimaEhMensagemAtual ? chatMensagens.slice(0, -1) : chatMensagens).map(
      (msg) => ({ autor: msg.autor, texto: msg.texto }),
    );
    return await negociacaoSelfService.responderSimulacao({
      leadId,
      accessToken,
      mensagem,
      historico,
    });
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao simular a resposta'));
  }
});

export const iniciarPagamentoSelf = createAsyncThunk<
  IniciarPagamentoResultado,
  string,
  { rejectValue: string; state: RootState }
>('negociacaoSelf/iniciarPagamento', async (variationId, { getState, rejectWithValue }) => {
  try {
    const { leadId, accessToken, objetivo, termosAceitos } = getState().negociacaoSelf;
    if (!leadId || !accessToken) {
      return rejectWithValue('Configuração incompleta. Volte ao início do fluxo.');
    }
    if (!termosAceitos) {
      return rejectWithValue(MENSAGEM_ACEITE_OBRIGATORIO);
    }
    const resultado = await negociacaoSelfService.iniciarPagamento({
      leadId,
      accessToken,
      variationId,
      aceiteTermos: termosAceitos,
      termosVersao: SELF_TERMOS_VERSAO,
    });
    if (resultado.status === 'CANCELADO' || resultado.status === 'ERRO') {
      limparSessaoSelf();
    } else {
      salvarSessaoSelf({
        leadId,
        accessToken,
        objetivo: objetivo ?? undefined,
        invoiceUrl: resultado.invoiceUrl,
        variationId,
        termosAceitos,
      });
    }
    return resultado;
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao iniciar o pagamento'));
  }
});

export const consultarPagamentoSelf = createAsyncThunk<
  ConsultaPagamentoResultado,
  void,
  { rejectValue: string; state: RootState }
>('negociacaoSelf/consultarPagamento', async (_, { getState, signal, rejectWithValue }) => {
  try {
    const { leadId, accessToken } = getState().negociacaoSelf;
    if (!leadId || !accessToken) {
      return rejectWithValue('Configuração incompleta. Volte ao início do fluxo.');
    }
    const resultado = await negociacaoSelfService.consultarPagamento(
      { leadId, accessToken },
      signal,
    );
    if (resultado.status === 'CANCELADO' || resultado.status === 'ERRO') {
      limparSessaoSelf();
    }
    return resultado;
  } catch (error) {
    return rejectWithValue(mensagemDeErro(error, 'Erro ao consultar o pagamento'));
  }
});
